
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy, computed, ChangeDetectorRef } from '@angular/core';
import { DatePipe, CommonModule } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { Prescription } from '../../models/index';
import { SmartPrescriptionModalComponent } from '../smart-prescription-modal/smart-prescription-modal.component';
import { VideoCallService } from '../../services/video-call.service';
import { VideoCallModalComponent } from '../video-call-modal/video-call-modal.component';
import { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '../../firebase';
import { NotificationService } from '../../services/notification.service'; 
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

type LoadingState = 'loading' | 'loaded' | 'error';
type QueueFilter = 'all' | 'walkin' | 'referral';

@Component({
  selector: 'app-tele-medicine',
  standalone: true,
  templateUrl: './tele-medicine.component.html',
  imports: [TranslatePipe, DatePipe, SmartPrescriptionModalComponent, VideoCallModalComponent, CommonModule, ConfirmationModalComponent],
  providers: [VideoCallService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeleMedicineComponent implements OnInit, OnDestroy {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private videoCallService = inject(VideoCallService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private supabase: SupabaseClient | null = getSupabase();

  prescriptions = signal<Prescription[]>([]);
  loadingState = signal<LoadingState>('loading');
  
  // UI State
  activeFilter = signal<QueueFilter>('all');
  isModalOpen = signal(false);
  selectedPrescription = signal<Prescription | null>(null);
  
  isCallModalOpen = signal(false);
  activeCallToken = signal<string | null>(null);
  activeRoomName = signal<string | null>(null);
  isCalling = signal(false); 
  isReleasing = signal(false);
  
  // Alert State
  hasNewPatientAlert = signal(false);

  // Confirmation Modal State
  confirmModalState = signal({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonText: 'Confirm',
    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
    action: null as (() => Promise<void>) | null
  });

  currentUser = this.authService.currentUser;
  isDoctor = computed(() => {
    const role = this.currentUser()?.role;
    return role === 'doctor' || role === 'admin' || role === 'owner';
  });

  myActiveCases = computed(() => {
    const myId = this.currentUser()?.uid;
    if (!myId) return [];
    return this.prescriptions().filter(p => p.status === 'ENGAGED' && p.engaged_by === myId);
  });

  generalQueue = computed(() => {
    const myId = this.currentUser()?.uid;
    let list = this.prescriptions().filter(p => !(p.status === 'ENGAGED' && p.engaged_by === myId));
    const filter = this.activeFilter();
    if (filter === 'walkin') list = list.filter(p => !p.referred_by_store_name);
    else if (filter === 'referral') list = list.filter(p => !!p.referred_by_store_name);
    return list;
  });

  private realtimeSubscription: any;

  ngOnInit(): void {
    this.loadPrescriptions();
    // Delay subscription slightly to ensure Auth is ready
    setTimeout(() => this.setupRealtimeSubscription(), 500);
  }

  ngOnDestroy(): void {
    if (this.realtimeSubscription) {
        this.supabase?.removeChannel(this.realtimeSubscription);
    }
    this.notificationService.stopIncomingAlert();
  }

  setupRealtimeSubscription() {
    if (!this.supabase) return;
    const storeId = this.authService.currentStore()?.id;
    if (!storeId) return;

    // Listen specifically to MY STORE channel
    const channelName = `store-channel-${storeId}`;
    console.log(`[Doctor] Connecting to Realtime Channel: ${channelName}`);
    
    // Remove existing if any to prevent duplicates
    if (this.realtimeSubscription) this.supabase.removeChannel(this.realtimeSubscription);

    this.realtimeSubscription = this.supabase.channel(channelName)
      // 1. Listen for Manual Broadcasts (Primary Method)
      .on(
        'broadcast',
        { event: 'new_patient' },
        (payload) => {
          console.log("[Realtime] 🔔 Broadcast Received:", payload);
          this.triggerNewPatientAlert();
          this.loadPrescriptions(true);
        }
      )
      // 2. Listen for Database Changes (Backup Method - PostgreSQL CDC)
      .on(
        'postgres_changes',
        { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'prescriptions'
        },
        (payload: any) => {
          // Check if this row relates to us
          const newRow = payload.new;
          if (newRow.store_id === storeId || newRow.referred_to_store_id === storeId) {
             console.log("[Realtime] 📥 DB Insert Detected:", newRow);
             this.triggerNewPatientAlert();
             this.loadPrescriptions(true);
          }
        }
      )
      .on(
        'postgres_changes',
        { 
            event: 'UPDATE', 
            schema: 'public', 
            table: 'prescriptions'
        },
        (payload: any) => {
             // Refresh on status changes (e.g. Pending -> Engaged)
             const newRow = payload.new;
             if (newRow.store_id === storeId || newRow.referred_to_store_id === storeId) {
                this.loadPrescriptions(true);
             }
        }
      )
      .subscribe((status) => {
         if (status === 'SUBSCRIBED') {
             console.log(`[Doctor] ✅ Successfully subscribed to ${channelName}`);
         } else if (status === 'CHANNEL_ERROR') {
             console.error(`[Doctor] ❌ Realtime Connection Error. Retrying in 5s...`);
             setTimeout(() => this.setupRealtimeSubscription(), 5000);
         }
      });
  }

  private triggerNewPatientAlert() {
      // Play sound
      this.notificationService.playQueuePing();
      
      // Visual flash
      this.hasNewPatientAlert.set(true);
      this.cdr.detectChanges();
      
      // Auto-hide alert after 5 seconds
      setTimeout(() => {
          this.hasNewPatientAlert.set(false);
          this.cdr.detectChanges();
      }, 5000);
  }

  async loadPrescriptions(isBackground: boolean = false): Promise<void> {
    if (!isBackground) this.loadingState.set('loading');
    try {
      const data = await this.dataService.getPendingPrescriptions();
      this.prescriptions.set(data);
      if (!isBackground) this.loadingState.set('loaded');
      this.cdr.markForCheck(); 
    } catch (e) {
      console.error(e);
      if (!isBackground) this.loadingState.set('error');
    }
  }

  setFilter(filter: QueueFilter) { this.activeFilter.set(filter); }

  async startCall(prescription: Prescription) {
    if (this.isCalling()) return;
    this.notificationService.stopIncomingAlert();
    this.isCalling.set(true);
    this.selectedPrescription.set(prescription);
    
    try {
        await this.dataService.engagePrescription(prescription.id);
        const { roomName } = await this.videoCallService.createRoomAndGetTokens(prescription.id);
        await this.videoCallService.sendCallInvitation(prescription.customerId, roomName);
        this.activeRoomName.set(roomName);
        this.isCallModalOpen.set(true);
        this.loadPrescriptions(true); 
    } catch(e: any) {
        this.notificationService.handleError(e);
    } finally {
        this.isCalling.set(false);
    }
  }

  async openReview(prescription: Prescription) {
    this.notificationService.stopIncomingAlert();
    try {
        await this.dataService.engagePrescription(prescription.id);
        this.selectedPrescription.set(prescription);
        this.isModalOpen.set(true);
        this.loadPrescriptions(true);
    } catch (e: any) {
        this.notificationService.handleError(e);
    }
  }

  releaseCase(prescription: Prescription) {
    this.confirmModalState.set({
        isOpen: true,
        title: 'ยืนยันการคืนเคส',
        message: 'คุณต้องการคืนเคสนี้สู่คิวกลางเพื่อให้แพทย์ท่านอื่นตรวจแทนหรือไม่?',
        confirmButtonText: 'คืนเคส (Release)',
        confirmButtonClass: 'bg-yellow-600 hover:bg-yellow-700',
        action: async () => {
            this.isReleasing.set(true);
            try {
              await this.dataService.releasePrescription(prescription.id);
              this.notificationService.showSuccess('คืนเคสสู่คิวกลางเรียบร้อยแล้ว');
              this.loadPrescriptions(true);
            } catch(e) {
              this.notificationService.showError('เกิดข้อผิดพลาดในการคืนเคส');
            } finally {
                this.isReleasing.set(false);
            }
        }
    });
  }

  closeConfirmModal() {
    this.confirmModalState.update(s => ({ ...s, isOpen: false }));
  }

  async onConfirmAction() {
    const action = this.confirmModalState().action;
    if (action) await action();
    this.closeConfirmModal();
  }

  handleReviewComplete() {
    this.isModalOpen.set(false);
    this.selectedPrescription.set(null);
    this.loadPrescriptions();
  }
  
  handleModalClose() {
      this.isModalOpen.set(false);
      this.selectedPrescription.set(null);
  }
}
